import {
  getRefundAccountWithSettings,
  deleteRefundAccount,
  saveRefundAccount,
} from '../../api/api'
import { call, put, takeLatest } from 'redux-saga/effects'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import { addError } from 'portal-modules/common/redux/modules/errors'
import { addSettings } from 'portal-modules/common/redux/modules/settings'

// action types
const REQUEST_REFUND_METHOD = 'rp/refundMethod/REQUEST_REFUND_METHOD'
const RECEIVE_REFUND_METHOD = 'rp/refundMethod/RECEIVE_REFUND_METHOD'
const DELETE_REFUND_METHOD = 'rp/refundMethod/DELETE_REFUND_METHOD'
const REFUND_METHOD_DELETED = 'rp/refundMethod/REFUND_METHOD_DELETED'
const ADD_REFUND_METHOD = 'rp/refundMethod/ADD_REFUND_METHOD'
const REFUND_METHOD_ADDED = 'rp/refundMethod/REFUND_METHOD_ADDED'
const UPDATE_REFUND_METHOD = 'rp/refundMethod/UPDATE_REFUND_METHOD'
const REFUND_METHOD_UPDATED = 'rp/refundMethod/REFUND_METHOD_UPDATED'
const PLAID_ADD_REFUND_METHOD = 'rp/refundMethod/PLAID_ADD_REFUND_METHOD'
const PLAID_UPDATE_REFUND_METHOD = 'rp/refundMethod/PLAID_UPDATE_REFUND_METHOD'

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_REFUND_METHOD:
      return { ...state, ...payload }
    case REFUND_METHOD_DELETED:
      return (
        Object.values(state).reduce((obj, method) => {
          if (method.id != payload) return obj
        }, {}) || {}
      )
    case REFUND_METHOD_ADDED:
      return { ...state, [payload.id]: { ...payload } }
    case REFUND_METHOD_UPDATED:
      return {
        ...state,
        [payload.id]: { ...(state[payload.id] || {}), ...payload },
      }
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators
export const requestRefundMethod = () => ({
  type: REQUEST_REFUND_METHOD,
})

export const receiveRefundMethod = payload => ({
  type: RECEIVE_REFUND_METHOD,
  payload,
})

export const deleteRefundMethod = (payload, callback) => ({
  type: DELETE_REFUND_METHOD,
  callback,
  payload,
})

export const deletedRefundMethod = payload => ({
  type: REFUND_METHOD_DELETED,
  payload,
})

export const addRefundMethod = createFormAction(ADD_REFUND_METHOD)

export const plaidAddRefundMethod = createFormAction(PLAID_ADD_REFUND_METHOD)

export const updateRefundMethod = createFormAction(UPDATE_REFUND_METHOD)

export const plaidUpdateRefundMethod = createFormAction(
  PLAID_UPDATE_REFUND_METHOD,
)

function* handleRefundMethodError({ message, fields }, { key }) {
  message = fields ? fields[0].message : message
  yield put(addError({ key, message }))
}

// sagas
function* fetchRefundMethod() {
  const refundMethods = yield call(getRefundAccountWithSettings)

  const { settings, refund_accounts } = refundMethods

  if (Array.isArray(refund_accounts)) {
    yield put(
      receiveRefundMethod(
        refund_accounts.reduce(
          (obj, method) => ({
            ...obj,
            [method.id]: method,
          }),
          {},
        ),
      ),
    )
  }

  yield put(addSettings({ refundMethod: settings }))
}

function* postDeleteRefundMethod({ payload }) {
  yield call(deleteRefundAccount, {
    id: payload,
  })

  yield put({ type: REFUND_METHOD_DELETED, payload })
}

function* postAddRefundMethod({ payload = {}, callback }) {
  const addedMethod = yield call(saveRefundAccount, payload)

  yield put({
    type: REFUND_METHOD_ADDED,
    payload: addedMethod,
  })

  if (typeof callback === 'function') {
    yield call(callback, addedMethod)
  }
}

function* postUpdateRefundMethod({ payload = {}, callback }) {
  const updatedMethod = yield call(saveRefundAccount, payload)

  yield put({
    type: REFUND_METHOD_UPDATED,
    payload: updatedMethod,
  })

  if (typeof callback === 'function') {
    yield call(callback, updatedMethod)
  }
}

function* watchRequestRefundMethod() {
  yield takeLatest(
    REQUEST_REFUND_METHOD,
    createRequestSaga(fetchRefundMethod, {
      key: 'refundMethod',
      message: __('Finding refund method'),
    }),
  )
}

function* watchDeleteRefundMethod() {
  yield takeLatest(
    DELETE_REFUND_METHOD,
    createRequestSaga(postDeleteRefundMethod, {
      key: 'refundMethod',
      message: __('Syncing updates'),
      successMessage: __('Refund method deleted'),
      write: true,
    }),
  )
}

function* watchAddRefundMethod() {
  yield takeLatest(
    ADD_REFUND_METHOD,
    createRequestSaga(postAddRefundMethod, {
      key: 'refundMethod',
      message: __('Verifying account accepts Fast Funds'),
      successMessage: __('Account verified and saved'),
      write: true,
      onError: handleRefundMethodError,
    }),
  )
}

function* watchPlaidAddRefundMethod() {
  yield takeLatest(
    PLAID_ADD_REFUND_METHOD,
    createRequestSaga(postAddRefundMethod, {
      key: 'refundMethod',
      message: __('Saving Refund Account'),
      successMessage: __('Successfully Saved Refund Account'),
      write: true,
      onError: handleRefundMethodError,
    }),
  )
}

function* watchUpdateRefundMethod() {
  yield takeLatest(
    UPDATE_REFUND_METHOD,
    createRequestSaga(postUpdateRefundMethod, {
      key: 'refundMethod',
      message: __('Verifying account accepts Fast Funds'),
      successMessage: __('Account verified and saved'),
      write: true,
      onError: handleRefundMethodError,
    }),
  )
}

function* watchPlaidUpdateRefundMethod() {
  yield takeLatest(
    PLAID_UPDATE_REFUND_METHOD,
    createRequestSaga(postUpdateRefundMethod, {
      key: 'refundMethod',
      message: __('Saving Refund Account'),
      successMessage: __('Successfully Saved Refund Account'),
      write: true,
      onError: handleRefundMethodError,
    }),
  )
}

export const sagas = [
  watchRequestRefundMethod,
  watchDeleteRefundMethod,
  watchAddRefundMethod,
  watchPlaidAddRefundMethod,
  watchPlaidUpdateRefundMethod,
  watchUpdateRefundMethod,
]
